// Phase 11 — persistent multi-symbol clipboard tray.
//
// Tiny external store (useSyncExternalStore) so the tray survives navigations
// without React context, and the tray UI shares one source of truth across
// every collect button site-wide. State is mirrored to localStorage on every
// mutation so reloads restore the user's collection.

import { useSyncExternalStore } from "react";

export const TRAY_STORAGE_KEY = "coolsymbols.tray.v1";
const MAX_TRAY_SIZE = 64;

type State = { items: string[] };

let state: State = { items: [] };
let hydrated = false;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function persist() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(TRAY_STORAGE_KEY, JSON.stringify(state.items));
  } catch {
    /* quota / private mode — silently ignore */
  }
}

function hydrate() {
  if (hydrated || typeof window === "undefined") return;
  hydrated = true;
  try {
    const raw = window.localStorage.getItem(TRAY_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      const items = parsed
        .filter((s): s is string => typeof s === "string" && s.length > 0)
        .slice(0, MAX_TRAY_SIZE);
      state = { items };
      emit();
    }
  } catch {
    /* corrupted — drop */
  }
}

function subscribe(fn: () => void): () => void {
  hydrate();
  listeners.add(fn);
  // Cross-tab sync — pick up changes from other tabs.
  const onStorage = (e: StorageEvent) => {
    if (e.key !== TRAY_STORAGE_KEY) return;
    hydrated = false;
    hydrate();
  };
  if (typeof window !== "undefined") {
    window.addEventListener("storage", onStorage);
  }
  return () => {
    listeners.delete(fn);
    if (typeof window !== "undefined") {
      window.removeEventListener("storage", onStorage);
    }
  };
}

const EMPTY: State = { items: [] };
function getSnapshot(): State {
  return state;
}
function getServerSnapshot(): State {
  return EMPTY;
}

/** React hook — re-renders whenever the tray mutates. */
export function useTray(): State {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export function addToTray(symbol: string): void {
  hydrate();
  if (!symbol) return;
  if (state.items.length >= MAX_TRAY_SIZE) return;
  if (state.items.includes(symbol)) return;
  state = { items: [...state.items, symbol] };
  persist();
  emit();
}

export function removeFromTray(symbol: string): void {
  hydrate();
  const next = state.items.filter((s) => s !== symbol);
  if (next.length === state.items.length) return;
  state = { items: next };
  persist();
  emit();
}

export function reorderTray(from: number, to: number): void {
  hydrate();
  if (from === to) return;
  if (from < 0 || from >= state.items.length) return;
  if (to < 0 || to >= state.items.length) return;
  const next = state.items.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  state = { items: next };
  persist();
  emit();
}

export function clearTray(): void {
  hydrate();
  if (state.items.length === 0) return;
  state = { items: [] };
  persist();
  emit();
}

export function isInTray(symbol: string): boolean {
  return state.items.includes(symbol);
}

export const TRAY_LIMIT = MAX_TRAY_SIZE;
